import type { LucideIcon } from 'lucide-react';
import { SiGoogle } from 'react-icons/si';
import {
	CheckCircle2,
	XCircle,
	Shield,
	ShieldX,
	Briefcase,
	UserX,
	AlertCircle,
	Calendar,
	Phone,
	Users as UsersIcon,
	Ban,
} from 'lucide-react';
import { maskPhone } from '@/utils/masks';

function formatDateTimePt(value?: string | null): string | undefined {
	if (!value) return undefined;
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return undefined;
	const date = d.toLocaleDateString('pt-BR');
	const time = d.toLocaleTimeString('pt-BR', {
		hour: '2-digit',
		minute: '2-digit',
	});
	return `${date} ${time}`;
}

const StatusItem = ({
	label,
	value,
	icon: Icon,
	active,
}: {
	label: string;
	value: string;
	icon: LucideIcon | typeof SiGoogle;
	active: boolean;
}) => (
	<div className="flex h-[56px] w-full min-w-0 items-center gap-3 rounded-2xl border border-zinc-100 bg-zinc-50 px-4 py-2 transition-all hover:bg-zinc-100">
		<div
			className={`shrink-0 rounded-lg p-1.5 ${
				active ? 'bg-zinc-900 text-white' : 'bg-zinc-200 text-zinc-500'
			}`}
		>
			<Icon size={14} />
		</div>
		<div className="flex min-w-0 flex-1 flex-col justify-center overflow-hidden">
			<span className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">
				{label}
			</span>
			<span className="truncate text-xs font-bold text-zinc-900">
				{value}
			</span>
		</div>
	</div>
);

type Variant = 'perfil' | 'acesso' | 'permissoes';

interface UserPatientDetailTagsProps {
	variant: Variant;
	user: Partial<Users.User>;
}

const UserPatientDetailTags = ({
	variant,
	user,
}: UserPatientDetailTagsProps) => {
	const createdAt = formatDateTimePt(user.created_at);
	const lastLogin = formatDateTimePt(user.last_login);
	const updatedAt = formatDateTimePt(user.updated_at);
	const phoneDigits = user.phone_number?.replace(/\D/g, '') ?? '';
	const phoneDisplay =
		phoneDigits.length > 0 ? maskPhone(user.phone_number!) : undefined;

	if (variant === 'perfil') {
		return (
			<div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
				<StatusItem
					label="Perfil"
					value={user.is_complete ? 'Completo' : 'Incompleto'}
					icon={AlertCircle}
					active={!!user.is_complete}
				/>
				<StatusItem
					label="Conta"
					value={user.banned ? 'Banido' : 'Ativo'}
					icon={CheckCircle2}
					active={!user.banned}
				/>
				<StatusItem
					label="Vínculo com empresa"
					value={
						user.employee
							? 'Funcionário'
							: 'Sem vínculo empresarial'
					}
					icon={user.employee ? Briefcase : UserX}
					active={!!user.employee}
				/>
				<StatusItem
					label="Telefone"
					value={phoneDisplay ?? 'Não informado'}
					icon={Phone}
					active={!!phoneDisplay}
				/>
				{user.is_member != null && (
					<StatusItem
						label="Membro"
						value={user.is_member ? 'Sim' : 'Não'}
						icon={UsersIcon}
						active={!!user.is_member}
					/>
				)}
			</div>
		);
	}

	if (variant === 'acesso') {
		return (
			<div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
				{createdAt && (
					<StatusItem
						label="Cadastro"
						value={createdAt}
						icon={Calendar}
						active
					/>
				)}
				{lastLogin && (
					<StatusItem
						label="Último acesso"
						value={lastLogin}
						icon={Calendar}
						active
					/>
				)}
				{updatedAt && (
					<StatusItem
						label="Editado em"
						value={updatedAt}
						icon={Calendar}
						active
					/>
				)}
				{user.last_ban && (
					<StatusItem
						label="Último banimento"
						value={formatDateTimePt(user.last_ban) ?? '---'}
						icon={AlertCircle}
						active={false}
					/>
				)}
				{user.last_ban_reason?.trim() && (
					<StatusItem
						label="Motivo do banimento"
						value={user.last_ban_reason.trim()}
						icon={AlertCircle}
						active={false}
					/>
				)}
			</div>
		);
	}

	const verifiedUnknown =
		user.verified_email !== true && user.verified_email !== false;
	const googleUnknown =
		user.google_permissions_allowed !== true &&
		user.google_permissions_allowed !== false;
	const bannedUnknown = user.banned !== true && user.banned !== false;

	return (
		<div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
			<StatusItem
				label="Situação da conta"
				value={bannedUnknown ? '---' : user.banned ? 'Banido' : 'Ativo'}
				icon={bannedUnknown ? Shield : user.banned ? Ban : CheckCircle2}
				active={!bannedUnknown && !user.banned}
			/>
			<StatusItem
				label="Permissão de Dados do Google"
				value={
					googleUnknown
						? '---'
						: user.google_permissions_allowed
						  ? 'Permissões concedidas no Google'
						  : 'Não Autorizado'
				}
				icon={
					googleUnknown
						? Shield
						: user.google_permissions_allowed
						  ? CheckCircle2
						  : XCircle
				}
				active={!googleUnknown && !!user.google_permissions_allowed}
			/>
			<StatusItem
				label="Verificação de E-mail"
				value={
					verifiedUnknown
						? '---'
						: user.verified_email
						  ? 'Verificado pelo Google'
						  : 'E-mail não verificado pelo Google'
				}
				icon={
					user.verified_email === true
						? SiGoogle
						: user.verified_email === false
						  ? ShieldX
						  : Shield
				}
				active={user.verified_email === true}
			/>
		</div>
	);
};

export { UserPatientDetailTags };
export type { UserPatientDetailTagsProps };
